import moment from "moment";
import "moment/locale/es";
import { fmtHoraMexico, fmtFechaHoraMexico } from "../Components/fechaHoraMexico";

moment.locale("es");

/**
 * Pdf/reporteFiscalPdf.js
 *
 * PDF genérico de un reporte tabular, con la paleta de marca BIWO. Lo usan los
 * reportes fiscales nuevos (Conciliacion Facturas - Pagos y Facturas Globales).
 *
 * Es deliberadamente tonto: recibe las columnas y las filas ya aplanadas por el
 * mismo constructor que alimenta el Excel, para que los dos archivos no puedan
 * decir cosas distintas. Aquí sólo se maqueta.
 *
 * Va en horizontal porque estos listados traen de 10 a 15 columnas.
 *
 * Mismo lenguaje visual que `creditosMovimientosPdf`, pero con soporte para
 * varias secciones (una por tabla) y una banda de indicadores arriba.
 */

const C_TURQUESA = "#00BFD6";
const C_TEAL_PRO = "#005F6B";
const C_BANDA_GR = "#BFEDF5";
const C_ZEBRA = "#F5FBFC";
const C_TINTA_SU = "#8C8C8C";
const C_BORDE = "#D9D9D9";

// LETTER horizontal (792 pt) menos los márgenes laterales de 22.
const ANCHO_UTIL = 748;

const fmtDinero = (valor) =>
  (Number(valor) || 0).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  });

const ALINEACION = {
  numero: "right",
  moneda: "right",
  centro: "center",
  fecha: "center",
  hora: "center",
  fechaHora: "center",
};

const textoCelda = ({ valor, tipo }) => {
  if (tipo === "moneda") {
    const numero = Number(valor);
    return Number.isFinite(numero) ? fmtDinero(numero) : "";
  }
  if (tipo === "numero") {
    const numero = Number(valor);
    return Number.isFinite(numero) ? String(numero) : "";
  }
  if (tipo === "fecha") {
    const fecha = moment(valor);
    return valor && fecha.isValid() ? fecha.format("DD/MM/YYYY") : "";
  }
  if (tipo === "hora") return fmtHoraMexico(valor);
  if (tipo === "fechaHora") return fmtFechaHoraMexico(valor);
  return valor === null || valor === undefined ? "" : String(valor);
};

/** Una sección = un subtítulo + su tabla. */
const construirSeccion = (seccion) => {
  const bloques = [];

  if (seccion.titulo) {
    bloques.push({ text: seccion.titulo, style: "seccion" });
  }

  // pdfmake truena si `table.body` viene vacío: una sección sin renglones se
  // resuelve con una leyenda, no con una tabla de cero filas.
  if (!seccion.filas || seccion.filas.length === 0) {
    bloques.push({ text: "Sin registros en el periodo.", style: "vacio" });
    return bloques;
  }

  const columnas = seccion.columnas || [];
  const totales = seccion.totales || [];
  const alineacionCol = columnas.map((columna) => ALINEACION[columna.tipo] || "left");

  const cuerpo = [
    columnas.map((columna, indice) => ({
      text: columna.titulo,
      style: "th",
      alignment: alineacionCol[indice],
    })),
    ...seccion.filas.map((fila, indice) => {
      const fondo = indice % 2 === 1 ? C_ZEBRA : null;
      return fila.map((celda) => ({
        text: textoCelda(celda),
        style: "celda",
        alignment: ALINEACION[celda.tipo] || "left",
        fillColor: celda.fillColor || fondo,
        // Mismo campo que consume el Excel (ExcelTablaReporte): tiñe solo la
        // letra, p.ej. los importes en negativo de los reportes financieros.
        ...(celda.colorTexto ? { color: celda.colorTexto } : {}),
      }));
    }),
  ];

  const hayTotales = totales.some(Boolean);
  if (hayTotales) {
    cuerpo.push(
      columnas.map((columna, indice) => {
        const base = { fillColor: C_BANDA_GR, style: "total" };
        if (indice === 0) return { ...base, text: "TOTAL" };
        const total = totales[indice];
        if (!total) return { ...base, text: "" };
        return {
          ...base,
          text: total.tipo === "moneda" ? fmtDinero(total.valor) : String(total.valor),
          alignment: "right",
        };
      })
    );
  }

  const iSobreTotal = hayTotales ? cuerpo.length - 1 : -1;

  bloques.push({
    table: {
      headerRows: 1,
      // Las columnas sin `anchoPdf` se reparten el espacio restante ("*"). Con
      // "auto" pdfmake las mide por contenido y una tabla ancha se sale de la hoja.
      widths: columnas.map((columna) => columna.anchoPdf || "*"),
      body: cuerpo,
    },
    layout: {
      // Sólo líneas horizontales, y ninguna bajo el encabezado: el relleno
      // turquesa ya lo separa. La raya gruesa va encima de la fila de TOTAL.
      hLineWidth: (i, node) => {
        if (i === 0 || i === 1) return 0;
        return i === iSobreTotal || i === node.table.body.length ? 0.8 : 0.4;
      },
      vLineWidth: () => 0,
      hLineColor: (i, node) =>
        i === iSobreTotal || i === node.table.body.length ? C_TEAL_PRO : C_BORDE,
      paddingTop: () => 2.5,
      paddingBottom: () => 2.5,
      paddingLeft: () => 3,
      paddingRight: () => 3,
    },
    margin: [0, 0, 0, 12],
  });

  return bloques;
};

/**
 * @param {object}   p
 * @param {string}   p.titulo               Nombre del reporte.
 * @param {string}   p.empresaNombre
 * @param {string}   [p.descripcionFiltros] Los filtros activos, en texto.
 * @param {object[]} [p.indicadores]        `[{ etiqueta, valor }]`, la banda de arriba.
 * @param {number}   [p.contador]           Registros; por omisión, los de la 1ª sección.
 * @param {object[]} p.secciones            `[{ titulo, columnas, filas, totales }]`
 * @param {string}   [p.imagen]             PNG en data URL, entre la banda y las
 *   tablas (una gráfica, normalmente). pdfmake 0.1.x no sabe dibujar SVG, así que
 *   tiene que llegar ya rasterizada.
 */
const reporteFiscalPdf = ({
  titulo, empresaNombre, descripcionFiltros, indicadores = [], contador, secciones = [],
  imagen,
}) => {
  const registros = contador !== undefined
    ? contador
    : (secciones[0] && secciones[0].filas ? secciones[0].filas.length : 0);

  const encabezado = {
    columns: [
      {
        width: "*",
        stack: [
          { text: "BIWO", style: "biwo" },
          { text: empresaNombre || "", style: "empresa", margin: [0, 4, 0, 0] },
          { text: titulo, style: "titulo" },
          ...(descripcionFiltros ? [{ text: descripcionFiltros, style: "meta" }] : []),
        ],
      },
      {
        width: "auto",
        stack: [
          { text: `${registros}`, style: "contadorNum", alignment: "right" },
          { text: "registro(s)", style: "contadorTxt", alignment: "right" },
        ],
      },
    ],
  };

  const regla = {
    canvas: [{ type: "rect", x: 0, y: 0, w: ANCHO_UTIL, h: 3, color: C_TURQUESA }],
    margin: [0, 8, 0, 10],
  };

  const banda = indicadores.length > 0
    ? [{
      columns: indicadores.map((indicador) => ({
        width: "*",
        stack: [
          { text: indicador.etiqueta, style: "kpiTxt" },
          { text: indicador.valor, style: "kpiNum" },
        ],
      })),
      margin: [0, 0, 0, 12],
    }]
    : [];

  const bloqueImagen = imagen
    ? [{ image: imagen, width: ANCHO_UTIL, margin: [0, 0, 0, 12] }]
    : [];

  const contenido = [];
  for (const seccion of secciones) contenido.push(...construirSeccion(seccion));

  return {
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [22, 22, 22, 30],
    footer: (pagina, total) => ({
      columns: [
        { text: `Generado ${moment().format("DD/MM/YYYY HH:mm")}`, style: "pie" },
        { text: `${pagina} / ${total}`, style: "pie", alignment: "right" },
      ],
      margin: [22, 8, 22, 0],
    }),
    content: [encabezado, regla, ...banda, ...bloqueImagen, ...contenido],
    styles: {
      biwo: { fontSize: 20, bold: true, color: C_TURQUESA },
      empresa: { fontSize: 13, bold: true },
      titulo: { fontSize: 10, bold: true, color: C_TEAL_PRO, margin: [0, 2, 0, 0] },
      meta: { fontSize: 7, color: C_TINTA_SU, margin: [0, 2, 0, 0] },
      contadorNum: { fontSize: 22, bold: true, color: C_TURQUESA },
      contadorTxt: { fontSize: 7, color: C_TINTA_SU },
      kpiTxt: { fontSize: 6.5, color: C_TINTA_SU },
      kpiNum: { fontSize: 11, bold: true, color: C_TEAL_PRO },
      seccion: { fontSize: 9, bold: true, color: C_TEAL_PRO, margin: [0, 4, 0, 5] },
      th: { fontSize: 7.5, bold: true, color: "#FFFFFF", fillColor: C_TURQUESA, margin: [0, 2, 0, 2] },
      celda: { fontSize: 7 },
      total: { fontSize: 7.5, bold: true, color: C_TEAL_PRO },
      vacio: { fontSize: 8, color: C_TINTA_SU, margin: [0, 0, 0, 12] },
      pie: { fontSize: 6.5, color: C_TINTA_SU },
    },
  };
};

export default reporteFiscalPdf;
